// src/shared/utils/manualSeatExitLock.js
// 정식 일어서기/테이블 나가기 이후 늦은 스냅샷 또는 recoveryTables가
// 이미 비운 좌석을 다시 복구하지 못하도록 하는 클라이언트 tombstone 유틸.

const STORAGE_KEY = 'npc:manualSeatExitLocks:v2';
const DEFAULT_TTL_MS = Number((typeof process !== 'undefined' && process?.env?.REACT_APP_RECENTLY_LEFT_TOMBSTONE_MS) || 60_000);

function nowMs() { return Date.now(); }
function canUseStorage() {
  try { return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'; } catch (_) { return false; }
}
function norm(v) { return String(v ?? '').trim(); }
function normLower(v) { return norm(v).toLowerCase(); }
function normSeat(v) {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}
function readMap() {
  if (!canUseStorage()) return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch (_) { return {}; }
}
function writeMap(map) {
  if (!canUseStorage()) return;
  try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(map || {})); } catch (_) {}
}
function keyOf({ tableCode, userCode, seatNumber } = {}) {
  const tc = norm(tableCode);
  const uc = norm(userCode);
  const sn = normSeat(seatNumber);
  if (tc && uc) return `${uc}::${tc}`;
  if (tc && sn) return `seat:${tc}:${sn}`;
  return '';
}
function isExpired(lock, ts = nowMs()) {
  const saved = Number(lock?.ts || 0);
  const rawTtl = lock?.ttl_ms ?? lock?.ttlMs ?? DEFAULT_TTL_MS;
  const ttl = Number(rawTtl);
  if (!saved) return true;
  // 구버전의 무기한 tombstone은 이후 정상 재접속 복구까지 막을 수 있다.
  // v2에서는 ttl <= 0도 기본 TTL로 제한한다.
  if (Number.isFinite(ttl) && ttl <= 0) return ts - saved > DEFAULT_TTL_MS;
  return ts - saved > (Number.isFinite(ttl) ? ttl : DEFAULT_TTL_MS);
}
function prune(map = {}, ts = nowMs()) {
  let changed = false;
  const out = {};
  for (const [k, v] of Object.entries(map || {})) {
    if (!v || typeof v !== 'object' || isExpired(v, ts)) { changed = true; continue; }
    out[k] = v;
  }
  if (changed) writeMap(out);
  return out;
}

export function getManualSeatExitLocks() { return prune(readMap()); }
export function getManualSeatExitLocksForTable(tableCode) {
  const tc = norm(tableCode);
  if (!tc) return [];
  return Object.values(getManualSeatExitLocks()).filter((lock) => norm(lock?.tableCode || lock?.table_code) === tc);
}
export function getManualSeatExitLock({ tableCode, userCode, seatNumber } = {}) {
  const tc = norm(tableCode);
  const uc = norm(userCode);
  const sn = normSeat(seatNumber);
  const locks = getManualSeatExitLocks();
  if (!tc) return null;

  // 1순위: user_code가 명확하면 user_code 기준만 강하게 매칭한다.
  // 같은 seat_number를 다른 유저가 재사용하는 경우까지 tombstone이 먹지 않게 하기 위함.
  if (uc) {
    for (const lock of Object.values(locks)) {
      if (norm(lock?.tableCode || lock?.table_code) !== tc) continue;
      if (!isManualExitLock(lock)) continue;
      if (lockMatchesUser(lock, uc)) return lock;
    }
    return null;
  }

  // 2순위: user_code가 없는 늦은 seat-only 스냅샷에만 seat_number fallback 허용.
  if (!sn) return null;
  for (const lock of Object.values(locks)) {
    if (norm(lock?.tableCode || lock?.table_code) !== tc) continue;
    if (!isManualExitLock(lock)) continue;
    if (lockMatchesSeat(lock, sn)) return lock;
  }
  return null;
}
export function isManualSeatExitLocked(args = {}) { return !!getManualSeatExitLock(args); }

export function markManualSeatExit({ tableCode, userCode, seatNumber, reason, source, ttlMs } = {}) {
  const tc = norm(tableCode);
  const uc = norm(userCode);
  const sn = normSeat(seatNumber);
  if (!tc || (!uc && !sn) || !canUseStorage()) return null;
  const ts = nowMs();
  const map = getManualSeatExitLocks();
  const lock = {
    tableCode: tc,
    table_code: tc,
    userCode: uc,
    user_code: uc,
    seatNumber: sn,
    seat_number: sn,
    reason: norm(reason || 'manual_standup'),
    source: norm(source || 'manual_seat_exit'),
    ts,
    ttl_ms: Number(ttlMs == null ? DEFAULT_TTL_MS : ttlMs),
  };
  map[keyOf({ tableCode: tc, userCode: uc, seatNumber: sn })] = lock;
  writeMap(map);
  try {
    for (const k of ['currentTableCode', 'lastTableCode', 'tableCode']) {
      if (norm(window.localStorage.getItem(k)) === tc) window.localStorage.removeItem(k);
    }
  } catch (_) {}
  // 팝업에서 나가기를 완료한 경우에도 데스크톱 메인창의 저장 탭 상태를 함께 제거한다.
  try {
    const desktopKey = 'desktop_multitable_v1';
    const raw = window.localStorage.getItem(desktopKey);
    if (raw) {
      const parsed = JSON.parse(raw);
      const screens = (Array.isArray(parsed?.screens) ? parsed.screens : []).filter((screen) => {
        const screenTableCode = norm(
          screen?.tableId ||
          (String(screen?.id || '').startsWith('table:') ? String(screen.id).slice('table:'.length) : '')
        );
        return !screenTableCode || screenTableCode !== tc;
      });
      const activeId = String(parsed?.activeId || 'lobby');
      const activeTableCode = activeId.startsWith('table:') ? norm(activeId.slice('table:'.length)) : '';
      window.localStorage.setItem(desktopKey, JSON.stringify({
        ...(parsed && typeof parsed === 'object' ? parsed : {}),
        screens: screens.some((screen) => screen?.id === 'lobby')
          ? screens
          : [{ id: 'lobby', type: 'lobby' }, ...screens],
        activeId: activeTableCode === tc ? 'lobby' : activeId,
      }));
    }
  } catch (_) {}
  try { window.dispatchEvent(new CustomEvent('npc:manualSeatExitLock', { detail: lock })); } catch (_) {}
  return lock;
}

export function clearManualSeatExit({ tableCode, userCode, seatNumber } = {}) {
  if (!canUseStorage()) return false;
  const tc = norm(tableCode);
  const uc = norm(userCode);
  const sn = normSeat(seatNumber);
  const map = getManualSeatExitLocks();
  let changed = false;
  if (tc && uc) {
    // user_code가 있으면 해당 사용자+테이블 tombstone만 해제한다.
    // 다른 사용자가 같은 seat_number에 앉았다는 이유로 내 명시적 나가기 기록이 지워지면 안 된다.
    const k = keyOf({ tableCode: tc, userCode: uc, seatNumber: sn });
    if (map[k]) { delete map[k]; changed = true; }
  } else if (tc && sn) {
    // user_code가 전혀 없는 레거시 seat-only 확정 이벤트에만 좌석 fallback을 허용한다.
    for (const [k, lock] of Object.entries(map)) {
      if (norm(lock?.tableCode || lock?.table_code) !== tc) continue;
      const lockedSeat = normSeat(lock?.seatNumber ?? lock?.seat_number ?? lock?.seat);
      if (lockedSeat && lockedSeat === sn) { delete map[k]; changed = true; }
    }
  }
  if (changed) writeMap(map);
  return changed;
}

export function isFreshSeatConfirmPayload(payload = {}) {
  const text = [payload?.reason, payload?.mode, payload?.type, payload?.event, payload?.sync_reason, payload?.syncReason, payload?.source, payload?.status]
    .map(normLower).join(' ');
  return (
    payload?.seatConfirmed === true ||
    payload?.seat_confirmed === true ||
    payload?.confirmed === true ||
    payload?.newSeatAssigned === true ||
    payload?.new_seat_assigned === true ||
    payload?.freshJoinTable === true ||
    payload?.fresh_join_table === true ||
    payload?.explicitReSeatSuccess === true ||
    payload?.explicit_reseat_success === true ||
    text.includes('confirmseatpayment') ||
    text.includes('seatconfirmed') ||
    text.includes('seat_confirmed') ||
    text.includes('sit_confirm') ||
    text.includes('sitconfirmed') ||
    text.includes('fresh_sit') ||
    text.includes('new_sit') ||
    text.includes('newseatassigned') ||
    text.includes('new_seat_assigned') ||
    text.includes('freshjointable') ||
    text.includes('fresh_join_table') ||
    text.includes('explicit_reseat') ||
    text.includes('reseat_success')
  );
}
export function clearManualSeatExitIfFreshSeatConfirm(payload = {}, fallback = {}) {
  if (!isFreshSeatConfirmPayload(payload)) return false;
  return clearManualSeatExit({
    tableCode: payload?.tableCode ?? payload?.table_code ?? fallback?.tableCode ?? fallback?.table_code,
    userCode: payload?.userCode ?? payload?.user_code ?? fallback?.userCode ?? fallback?.user_code,
    seatNumber: payload?.seatNumber ?? payload?.seat_number ?? payload?.seat ?? fallback?.seatNumber ?? fallback?.seat_number,
  });
}

function _payloadText(payload = {}) {
  return [
    payload?.reason,
    payload?.mode,
    payload?.type,
    payload?.event,
    payload?.source,
    payload?.status,
    payload?.sync_reason,
    payload?.syncReason,
    payload?.sync_kind,
    payload?.syncKind,
    payload?.sync_scope,
    payload?.syncScope,
    payload?.snapshotKind,
    payload?.snapshot_kind,
    payload?.applyPolicy,
    payload?.apply_policy,
    payload?.legacy_event,
    payload?.legacyEvent,
  ].map(normLower).join(' ');
}

export function isRecoveryOrRejoinPayload(payload = {}) {
  if (!payload || typeof payload !== 'object') return false;
  if (isFreshSeatConfirmPayload(payload)) return true;
  const text = _payloadText(payload);
  return (
    payload?.recovery === true ||
    payload?.is_recovery === true ||
    payload?.isRecovery === true ||
    payload?.rejoin === true ||
    payload?.is_rejoin === true ||
    payload?.isRejoin === true ||
    payload?.rejoined === true ||
    payload?.restored === true ||
    payload?.needReconnect === true ||
    payload?.need_reconnect === true ||
    text.includes('recovery') ||
    text.includes('recover') ||
    text.includes('rejoin') ||
    text.includes('restore') ||
    text.includes('resume') ||
    text.includes('reconnect') ||
    text.includes('joinedtable') ||
    text.includes('joined_table') ||
    text.includes('fresh_join') ||
    text.includes('freshjointable') ||
    text.includes('admission') ||
    text.includes('admit')
  );
}

function isNonManualDisconnectText(text) {
  return (
    text.includes('disconnect') || text.includes('reconnect') || text.includes('network') || text.includes('offline') ||
    text.includes('refresh') || text.includes('reload') || text.includes('beforeunload') ||
    text.includes('window_close') || text.includes('windowclose') || text.includes('browser_close') ||
    text.includes('tab_close') || text.includes('close_reserved') || text.includes('window_close_reserved')
  );
}
export function shouldTreatAsManualSeatExit(payload = {}) {
  const text = _payloadText(payload);
  if (!text) return false;
  // seatLeft/kick/seat_clear는 원인이 disconnect_deadline이어도 서버가 좌석 제거를 확정한 이벤트다.
  // 늦은 snapshot이 유령 프로필/0자산을 되살리지 못하게 tombstone 대상으로 본다.
  if (
    text.includes('seatleft') || text.includes('seat_left') || text.includes('seat_clear') ||
    text.includes('kick') || text.includes('kicked')
  ) return true;
  if (isNonManualDisconnectText(text)) return false;
  return (
    payload?.manual === true || payload?.manual_exit === true || payload?.manualExit === true ||
    text.includes('manual_exit') || text.includes('manual_seat_exit') ||
    text.includes('manual_immediate_leave') || text.includes('manual_standup') || text.includes('manual_stand_up') ||
    text.includes('standup_done') || text.includes('exit_done') ||
    text.includes('seatleft') || text.includes('seat_left') || text.includes('seat_clear') ||
    text.includes('kick') || text.includes('kicked') ||
    text.includes('standup_button') || text.includes('stand_up_button') || text.includes('standup') || text.includes('stand_up') ||
    text.includes('leavetable') || text.includes('leave_table')
  );
}

function isManualExitLock(lock = {}) {
  return shouldTreatAsManualSeatExit(lock);
}

function lockMatchesUser(lock = {}, userCode = '') {
  const uc = norm(userCode);
  const lockedUc = norm(lock?.userCode ?? lock?.user_code);
  return !!(uc && lockedUc && uc === lockedUc);
}

function lockMatchesSeat(lock = {}, seatNumber = null) {
  const sn = normSeat(seatNumber);
  const lockedSeat = normSeat(lock?.seatNumber ?? lock?.seat_number ?? lock?.seat);
  return !!(sn && lockedSeat && sn === lockedSeat);
}

export function normalizeRecoveryTablesPayload(payload = {}, fallbackUserCode = '') {
  const raw = Array.isArray(payload?.tables) ? payload.tables : Array.isArray(payload) ? payload : [];
  const fallbackUc = norm(fallbackUserCode);
  const tables = raw
    .map((t) => ({
      ...t,
      tableCode: norm(t?.tableCode ?? t?.table_code ?? t?.table),
      table_code: norm(t?.tableCode ?? t?.table_code ?? t?.table),
      seatNumber: normSeat(t?.seatNumber ?? t?.seat_number ?? t?.seat),
      seat_number: normSeat(t?.seatNumber ?? t?.seat_number ?? t?.seat),
    }))
    .filter((t) => {
      if (!t.tableCode) return false;
      const uc = norm(t?.userCode ?? t?.user_code ?? fallbackUc);
      // recoveryTables는 서버 복구 진입 후보이므로 seat_number-only tombstone으로 숨기지 않는다.
      return !(uc && getManualSeatExitLock({ tableCode: t.tableCode, userCode: uc }));
    });
  return { ...(payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : {}), ts: Number(payload?.ts || nowMs()), tables };
}

function clearSeatRow(row = {}, seatNumber = null) {
  const sn = normSeat(seatNumber ?? row?.seat_number ?? row?.seatNumber ?? row?.seat);
  return {
    ...row,
    seat_number: sn ?? row?.seat_number ?? row?.seatNumber ?? row?.seat ?? null,
    seatNumber: sn ?? row?.seatNumber ?? row?.seat_number ?? row?.seat ?? null,
    user_code: null,
    userCode: null,
    nickname: null,
    nickName: null,
    profile_image: null,
    profileImage: null,
    asset: 0,
    balance: 0,
    chips: 0,
    stack: 0,
    bet_amount: 0,
    betAmount: 0,
    forced_extra: 0,
    forcedExtra: 0,
    seat_clear: true,
    seatClear: true,
    empty: true,
    is_empty: true,
    isEmpty: true,
    status: 'empty',
    is_connected: true,
    isConnected: true,
  };
}

export function scrubManualExitedSeatFromSnapshot(snapshot = {}, fallback = {}) {
  if (!snapshot || typeof snapshot !== 'object') return snapshot;

  // 새 착석 확정만 tombstone 우회/해제를 허용한다.
  // recovery/rejoin/requestTableInfo 계열은 네트워크 복구에는 필요하지만,
  // 방금 명시적으로 일어선 내 좌석을 늦은 스냅샷으로 되살리면 안 되므로
  // manual tombstone이 남아 있으면 아래 scrub 로직을 그대로 태운다.
  if (isFreshSeatConfirmPayload(snapshot)) return snapshot;

  const tableCode = norm(snapshot?.tableCode ?? snapshot?.table_code ?? snapshot?.meta?.tableCode ?? snapshot?.meta?.table_code ?? fallback?.tableCode ?? fallback?.table_code);
  if (!tableCode) return snapshot;

  const fallbackUserCode = norm(fallback?.userCode ?? fallback?.user_code);
  const fallbackSeatNumber = normSeat(fallback?.seatNumber ?? fallback?.seat_number ?? fallback?.seat);
  const locks = getManualSeatExitLocksForTable(tableCode).filter(isManualExitLock);
  if (!locks.length) return snapshot;

  const shouldClear = (row = {}, seatHint = null) => {
    const uc = norm(row?.user_code ?? row?.userCode ?? row?.user ?? row?.uid ?? row?.user_id);
    const sn = normSeat(row?.seat_number ?? row?.seatNumber ?? row?.seat ?? seatHint);

    // user_code가 명확한 좌석은 user_code 기준으로만 판단한다.
    // 내 tombstone이면 제거, 다른 유저면 절대 제거하지 않는다.
    if (uc) {
      return locks.some((lock) => lockMatchesUser(lock, uc));
    }

    // user_code가 없는 늦은/불완전 스냅샷만 seat_number fallback을 아주 제한적으로 허용한다.
    if (!sn) return false;
    return locks.some((lock) => {
      if (!lockMatchesSeat(lock, sn)) return false;
      const lockedUc = norm(lock?.userCode ?? lock?.user_code);
      if (lockedUc && fallbackUserCode && lockedUc !== fallbackUserCode) return false;
      const lockedSeat = normSeat(lock?.seatNumber ?? lock?.seat_number ?? lock?.seat);
      return !!(lockedSeat && (!fallbackSeatNumber || lockedSeat === fallbackSeatNumber || lockMatchesUser(lock, fallbackUserCode)));
    });
  };

  let changed = false;
  const out = { ...snapshot };
  const seatRowByNumber = new Map();
  if (Array.isArray(snapshot.seats)) {
    const seatsArePrimitiveLayout = snapshot.seats.every((row) => !(row && typeof row === 'object'));
    for (const row of snapshot.seats) {
      const sn = normSeat(row && typeof row === 'object' ? (row?.seat_number ?? row?.seatNumber ?? row?.seat) : row);
      if (sn && row && typeof row === 'object') seatRowByNumber.set(sn, row);
    }

    // [FIX][ANCHOR:MANUAL_EXIT_LOCK:KEEP_NUMERIC_SEAT_LAYOUT]
    // StateSync public FULL은 보통 seats: [1,2,3...] + assetsBySeat 형태다.
    // 여기서 잠긴 좌석 1개를 clearSeatRow 객체로 바꾸면 seats 배열이
    // [object, number, number...] 혼합형이 되고, 기존 정규화기가 첫 원소 기준으로
    // object[] 경로를 타면서 나머지 숫자 좌석의 assetsBySeat를 잃어버린다.
    // 결과적으로 재입장 직후 테이블 전체가 빈 것처럼 보일 수 있으므로,
    // 숫자 layout 배열은 절대 객체로 바꾸지 않고 assets/players 맵만 scrub한다.
    if (seatsArePrimitiveLayout) {
      out.seats = snapshot.seats.slice();
    } else {
      out.seats = snapshot.seats.map((row) => {
        if (row && typeof row === 'object') {
          if (shouldClear(row)) { changed = true; return clearSeatRow(row); }
          return row;
        }
        return row;
      });
    }
  }
  const scrubMap = (map) => {
    if (!map || typeof map !== 'object' || Array.isArray(map)) return map;
    let mapChanged = false;
    const next = { ...map };
    for (const [k, v] of Object.entries(map)) {
      const primitiveMapValue = !(v && typeof v === 'object');
      const rawRow = primitiveMapValue ? {} : v;
      const sn = normSeat(rawRow?.seat_number ?? rawRow?.seatNumber ?? rawRow?.seat ?? k);
      const authoritativeSeatRow = sn ? seatRowByNumber.get(sn) : null;
      // assetsBySeat처럼 값이 숫자뿐인 맵은 seats[]에 같은 좌석의 다른 유저가 명확하면 절대 지우지 않는다.
      // 이 방어가 없으면 내 tombstone이 새로 앉은 다른 유저의 자산 맵까지 지워 0/fallback 표시를 만들 수 있다.
      const row = primitiveMapValue && authoritativeSeatRow ? authoritativeSeatRow : rawRow;
      if (shouldClear(row, sn)) {
        delete next[k];
        if (sn != null) { delete next[sn]; delete next[String(sn)]; }
        mapChanged = true;
      }
    }
    if (mapChanged) changed = true;
    return mapChanged ? next : map;
  };
  out.assetsBySeat = scrubMap(snapshot.assetsBySeat);
  out.assets_by_seat = scrubMap(snapshot.assets_by_seat);
  out.assets = scrubMap(snapshot.assets);
  out.seatAssets = scrubMap(snapshot.seatAssets);
  out.seat_assets = scrubMap(snapshot.seat_assets);
  out.players = scrubMap(snapshot.players);
  out.playersMap = scrubMap(snapshot.playersMap);
  out.players_map = scrubMap(snapshot.players_map);
  out.betsBySeat = scrubMap(snapshot.betsBySeat);
  out.bets_by_seat = scrubMap(snapshot.bets_by_seat);
  out.actionsBySeat = scrubMap(snapshot.actionsBySeat);
  out.actions_by_seat = scrubMap(snapshot.actions_by_seat);
  out.forcedExtrasBySeat = scrubMap(snapshot.forcedExtrasBySeat);
  out.forced_extras_by_seat = scrubMap(snapshot.forced_extras_by_seat);
  const mySeat = normSeat(out.mySeatNumber ?? out.my_seat_number);
  if (mySeat && shouldClear({ seat_number: mySeat }, mySeat)) { delete out.mySeatNumber; delete out.my_seat_number; changed = true; }
  const actingSeat = normSeat(out.actingSeat ?? out.acting_seat);
  if (actingSeat && shouldClear({ seat_number: actingSeat }, actingSeat)) { out.actingSeat = null; out.acting_seat = null; changed = true; }
  return changed ? out : snapshot;
}


// [ADD][ANCHOR:RECENTLY_LEFT_TOMBSTONE_ALIASES]
// 최근 seatLeft/seat_clear/standup_done/kicked 전용 짧은 TTL tombstone alias.
export const markRecentlyLeftSeat = markManualSeatExit;
export const clearRecentlyLeftSeat = clearManualSeatExit;
export const getRecentlyLeftSeatTombstone = getManualSeatExitLock;
export const isRecentlyLeftSeatLocked = isManualSeatExitLocked;
export const scrubRecentlyLeftSeatFromSnapshot = scrubManualExitedSeatFromSnapshot;
